import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ChurchBrandMark } from '@/components/church-brand-mark';
import { GivePageShell } from '@/components/give-page-shell';
import { PageLoading, PageMessage } from '@/components/page-state';
import { PoweredByFooter } from '@/components/powered-by-footer';
import { SegmentTiles } from '@/components/segment-tiles';
import { api, ApiError } from '@/lib/api';
import { formatMoney } from '@/lib/format';
import type { RegistrationPage, RegistrationResult } from '@/types/registration';

const gatewayPath = (churchSlug: string, programId: string) =>
  `/gateway/register/${encodeURIComponent(churchSlug)}/${encodeURIComponent(programId)}`;

type LoadState = 'loading' | 'ready' | 'notFound' | 'error' | 'done';
type PaymentMethod = 'mobile_money' | 'card' | 'cash';

export const RegisterPage = () => {
  const { t, i18n } = useTranslation();
  const { churchSlug = '', programId = '' } = useParams();
  const [page, setPage] = useState<RegistrationPage | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [guestCount, setGuestCount] = useState('0');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);
  const [doneMessage, setDoneMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!churchSlug || !programId) return;

    const load = async () => {
      setLoadState('loading');
      try {
        const data = await api.get<RegistrationPage>(gatewayPath(churchSlug, programId));
        setPage(data);
        const methods = data.program.registrationAllowedPaymentMethods;
        if (data.program.registrationFeeType === 'paid' && methods.length > 0) {
          setPaymentMethod(methods[0] ?? null);
        }
        setLoadState('ready');
      } catch (error) {
        setPage(null);
        if (error instanceof ApiError && (error.code === 'PROGRAM_NOT_FOUND' || error.status === 404)) {
          setLoadState('notFound');
          return;
        }
        setLoadState('error');
      }
    };

    void load();
  }, [churchSlug, programId]);

  const isPaid = page?.program.registrationFeeType === 'paid';
  const currency = page?.program.registrationFeeCurrency ?? 'GHS';
  const feeMinor = page?.program.registrationFeeAmountMinor ?? null;

  const paymentOptions = useMemo(() => {
    if (!page || !isPaid) return [];
    return page.program.registrationAllowedPaymentMethods.map((method) => ({
      value: method,
      label: t(`register.payment.${method}`),
    }));
  }, [isPaid, page, t]);

  const canSubmit =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    phone.trim().length >= 6 &&
    (!isPaid || paymentMethod !== null);

  const onRegister = useCallback(async () => {
    if (!page || !canSubmit) return;

    setSubmitting(true);
    setSubmitErrorMessage(null);

    try {
      const returnUrl = window.location.href;
      const response = await api.post<RegistrationResult>(gatewayPath(churchSlug, programId), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        email: email.trim() || undefined,
        guestCount: Number(guestCount) || 0,
        paymentMethod: isPaid ? paymentMethod ?? undefined : undefined,
        returnUrl,
        cancelUrl: returnUrl,
      });

      const payment = response.payment;
      if (payment && 'outcome' in payment && payment.outcome === 'cash') {
        setDoneMessage(t('register.done.cash'));
        setLoadState('done');
        return;
      }

      const redirectUrl =
        payment && 'authorisationUrl' in payment
          ? payment.authorisationUrl ??
            payment.redirectUrl ??
            (payment.outcome?.kind === 'redirect' ? payment.outcome.authorisationUrl : undefined)
          : undefined;

      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }

      if (response.registration.status === 'pending_payment') {
        setDoneMessage(t('register.done.pending'));
      } else {
        setDoneMessage(t('register.done.confirmed'));
      }
      setLoadState('done');
    } catch (error) {
      if (error instanceof ApiError && error.code === 'ALREADY_REGISTERED') {
        setDoneMessage(t('register.errors.alreadyRegistered'));
        setLoadState('done');
        return;
      }

      if (error instanceof ApiError && error.code) {
        const key = `register.errors.${error.code}`;
        const translated = t(key, { defaultValue: '' });
        setSubmitErrorMessage(translated || t('register.submitError'));
      } else {
        setSubmitErrorMessage(t('register.submitError'));
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit,
    churchSlug,
    email,
    firstName,
    guestCount,
    isPaid,
    lastName,
    page,
    paymentMethod,
    phone,
    programId,
    t,
  ]);

  if (loadState === 'loading') {
    return <PageLoading />;
  }

  if (loadState === 'notFound') {
    return <PageMessage message={t('register.states.notFound')} />;
  }

  if (loadState === 'error' || !page) {
    return <PageMessage message={t('states.error')} />;
  }

  if (loadState === 'done' && doneMessage) {
    return (
      <GivePageShell footer={<PoweredByFooter />}>
        <div className="give-card give-card--form stack">
          <ChurchBrandMark
            churchName={page.church.name}
            campusName={page.church.campusName}
            logoUrl={page.church.logoUrl}
            heroImageUrl={page.program.coverImageUrl}
            title={page.program.name}
            description={page.program.description}
          />
          <p className="give-message">{doneMessage}</p>
        </div>
      </GivePageShell>
    );
  }

  const showPaymentColumn = isPaid && paymentOptions.length > 0;

  const feeCallout =
    feeMinor !== null ? (
      <div className="register-fee">
        <p className="register-fee__label">{t('register.feeLabel')}</p>
        <p className="register-fee__amount">{formatMoney(feeMinor, currency, i18n.language)}</p>
      </div>
    ) : null;

  const registrationFields = (
    <>
      <div className="give-field">
        <label className="field-label" htmlFor="register-first-name">
          {t('register.firstName')}
        </label>
        <input
          id="register-first-name"
          className="give-control"
          value={firstName}
          disabled={submitting}
          autoComplete="given-name"
          onChange={(event) => setFirstName(event.target.value)}
        />
      </div>

      <div className="give-field">
        <label className="field-label" htmlFor="register-last-name">
          {t('register.lastName')}
        </label>
        <input
          id="register-last-name"
          className="give-control"
          value={lastName}
          disabled={submitting}
          autoComplete="family-name"
          onChange={(event) => setLastName(event.target.value)}
        />
      </div>

      <div className="give-field">
        <label className="field-label" htmlFor="register-phone">
          {t('register.phone')}
        </label>
        <input
          id="register-phone"
          className="give-control"
          type="tel"
          value={phone}
          disabled={submitting}
          autoComplete="tel"
          onChange={(event) => setPhone(event.target.value)}
        />
      </div>

      <div className="give-field">
        <label className="field-label" htmlFor="register-email">
          {t('register.email')}
        </label>
        <input
          id="register-email"
          className="give-control"
          type="email"
          value={email}
          disabled={submitting}
          autoComplete="email"
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>

      <div className="give-field">
        <label className="field-label" htmlFor="register-guests">
          {t('register.guests')}
        </label>
        <input
          id="register-guests"
          className="give-control"
          type="number"
          min={0}
          max={20}
          value={guestCount}
          disabled={submitting}
          onChange={(event) => setGuestCount(event.target.value)}
        />
      </div>
    </>
  );

  const paymentSection =
    showPaymentColumn ? (
      <section className="stack-tight">
        {feeCallout}
        <p className="field-label">{t('register.paymentLabel')}</p>
        <SegmentTiles
          ariaLabel={t('register.paymentLabel')}
          options={paymentOptions}
          value={paymentMethod}
          onChange={(value) => setPaymentMethod(value as PaymentMethod)}
          disabled={submitting}
        />
        {paymentMethod === 'cash' ? (
          <p className="give-message">{t('register.cashHint')}</p>
        ) : null}
      </section>
    ) : null;

  return (
    <GivePageShell footer={<PoweredByFooter />} wide={showPaymentColumn}>
      <div className="give-card give-card--form stack">
        <ChurchBrandMark
          churchName={page.church.name}
          campusName={page.church.campusName}
          logoUrl={page.church.logoUrl}
          heroImageUrl={page.program.coverImageUrl}
          title={page.program.name}
          description={page.program.description}
        />

        {showPaymentColumn ? (
          <div className="register-columns">
            <div className="register-columns__fields stack">{registrationFields}</div>
            <aside className="register-columns__payment">{paymentSection}</aside>
          </div>
        ) : (
          <>
            {feeCallout}
            <div className="stack">{registrationFields}</div>
          </>
        )}

        {submitErrorMessage ? (
          <p className="give-message give-message--error" role="alert">
            {submitErrorMessage}
          </p>
        ) : null}

        <div className="give-actions give-actions--wide">
          <button
            type="button"
            className="primary"
            disabled={!canSubmit || submitting}
            onClick={() => void onRegister()}
          >
            {submitting ? t('register.submitting') : t('register.submit')}
          </button>
        </div>
      </div>
    </GivePageShell>
  );
};
